import React, { FC, useEffect, useRef, useLayoutEffect, useState } from 'react';
import { ChevronRight } from 'lucide-react';

export interface MenuItem {
  label?: React.ReactNode;
  icon?: React.ComponentType<{ size?: number; opacity?: number }>;
  action?: () => void;
  subItems?: MenuItem[];
  disabled?: boolean;
  isDivider?: boolean;
  shortcut?: string;
  className?: string;
}

interface ContextMenuProps {
  x: number;
  y: number;
  items: MenuItem[];
  onClose: () => void;
  align?: 'top' | 'bottom';
}

const getMaxMenuDepth = (items: MenuItem[]): number => {
  let max = 0;
  for (const item of items) {
    if (item.subItems) {
      max = Math.max(max, 1 + getMaxMenuDepth(item.subItems));
    }
  }
  return max;
};

const MenuList: FC<{ items: MenuItem[]; onClose: () => void; isSubmenu?: boolean; flipLeft?: boolean; flipUp?: boolean }> = ({ items, onClose, isSubmenu, flipLeft, flipUp }) => {
  return (
    <div className={`${isSubmenu ? "context-menu-submenu" : ""} ${flipLeft ? "flip-left" : ""} ${flipUp ? "flip-up" : ""}`}>
      {items.map((item, idx) => {
        if (item.isDivider) {
          return <div key={idx} className="context-menu-divider" />;
        }
        const Icon = item.icon as any;
        return (
          <div
            key={idx}
            className={`context-menu-item ${item.disabled ? 'disabled' : ''}`}
            onClick={(e) => {
              e.stopPropagation();
              if (item.disabled) return;
              if (item.action) {
                item.action();
                onClose();
              }
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
              {Icon && <Icon size={14} opacity={0.7} />}
              <span>{item.label}</span>
            </div>
            {item.shortcut && (
              <div style={{ 
                fontSize: '0.7rem', 
                opacity: 0.4, 
                marginLeft: '12px', 
                fontWeight: 500,
                letterSpacing: '0.02em'
              }}>
                {item.shortcut}
              </div>
            )}
            {item.subItems && <ChevronRight size={14} opacity={0.5} />}
            {item.subItems && (
               <MenuList items={item.subItems} onClose={onClose} isSubmenu flipLeft={flipLeft} flipUp={flipUp} />
            )}
          </div>
        );
      })}
    </div>
  );
};

export const ContextMenu: FC<ContextMenuProps> = ({ x, y, items, onClose, align }) => {
  const menuRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ x, y });
  const [flipLeft, setFlipLeft] = useState(false);
  const [flipUp, setFlipUp] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  useLayoutEffect(() => {
    if (menuRef.current) {
      const rect = menuRef.current.getBoundingClientRect();
      const padding = 24; 
      let newX = x;
      let newY = y;
      let shouldFlipUp = false;

      // Check right overflow for main menu
      if (x + rect.width > window.innerWidth - padding) {
        newX = window.innerWidth - rect.width - padding;
      }

      // Check bottom overflow or explicit alignment
      if (align === 'bottom' || (y + rect.height > window.innerHeight - padding)) {
        shouldFlipUp = true;
        // Position the bottom of the menu at the y coordinate
        newY = y - rect.height;
        
        // If it overflows the top, clamp it instead
        if (newY < padding) {
          newY = (align === 'bottom' && y + rect.height < window.innerHeight - padding) 
            ? y // If we wanted bottom alignment but it overflows top, and there is space below, just start at y
            : window.innerHeight - rect.height - padding;
          
          // If we clamped it and it now fits better below, don't flip submenus up
          if (newY + rect.height < window.innerHeight - padding) {
             shouldFlipUp = false;
          }
        }
      }

      // Ensure not off-screen left/top
      newX = Math.max(padding, newX);
      newY = Math.max(padding, newY);

      setPos({ x: newX, y: newY });
      setFlipUp(shouldFlipUp);

      // Determine if submenus should flip left based on total depth
      const maxDepth = getMaxMenuDepth(items);
      const submenuWidth = 180; // Approximate
      const totalWidthNeeded = rect.width + (maxDepth * submenuWidth);
      
      const spaceOnRight = window.innerWidth - padding - newX;
      const spaceOnLeft = newX - padding;
      
      // Flip left if we don't have space on the right for all submenus, 
      // AND we have more space on the left than on the right.
      const shouldFlipLeft = spaceOnRight < totalWidthNeeded && spaceOnLeft > spaceOnRight;
      
      setFlipLeft(shouldFlipLeft);
      setIsVisible(true);
    }
  }, [x, y, items, align]);

  return (
    <div 
      ref={menuRef} 
      className={`context-menu ${flipLeft ? 'flip-left' : ''} ${flipUp ? 'flip-up' : ''}`}
      style={{ 
        position: 'fixed', 
        left: pos.x, 
        top: pos.y, 
        zIndex: 2000,
        opacity: isVisible ? 1 : 0,
        pointerEvents: isVisible ? 'auto' : 'none',
        transition: 'opacity 0.15s ease-out'
      }}
    >
       <MenuList items={items} onClose={onClose} flipLeft={flipLeft} flipUp={flipUp} />
    </div>
  );
};

